import { readNumber, readNullableOneOf, readOneOf, readRecord, readRecords, readString, readStringList } from '@/lib/readers';
import type { LiveRow } from '@/lib/pendingWrites';

export const CAMPAIGN_STATUSES = ['draft', 'complete', 'deactivated'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CAMPAIGN_GOALS = ['awareness', 'purchases', 'lead'] as const;
export type CampaignGoal = (typeof CAMPAIGN_GOALS)[number];

export const MESSAGING_ANGLES = [
  'practical',
  'aspirational',
  'belonging',
  'value',
  'reassurance',
  'other',
] as const;
export type MessagingAngle = (typeof MESSAGING_ANGLES)[number];

export const VISUAL_TREATMENTS = ['photorealistic', 'stylised', 'ai_native'] as const;
export type VisualTreatment = (typeof VISUAL_TREATMENTS)[number];

export const PEOPLE_SETTINGS = ['no_people', 'people'] as const;
export type PeopleSetting = (typeof PEOPLE_SETTINGS)[number];

export const AUDIENCE_GENDERS = ['any', 'female', 'male'] as const;
export type AudienceGender = (typeof AUDIENCE_GENDERS)[number];

/** The ages an audience bracket accepts at each end. */
export const MINIMUM_AGE = 13;
export const MAXIMUM_AGE = 120;

/** Returns true when a bracket names two ages the API accepts, the lower one first. */
export function isNamedSegment(segment: AudienceSegment): boolean {
  const inRange = (age: number) => age >= MINIMUM_AGE && age <= MAXIMUM_AGE;
  return inRange(segment.ageFrom) && inRange(segment.ageTo) && segment.ageFrom <= segment.ageTo;
}

/** The briefing fields an uploaded brief can fill, by the key the extraction reports. */
export const BRIEFING_FIELDS = [
  'goal',
  'duration',
  'budget',
  'market',
  'segments',
  'direction',
  'angles',
  'treatments',
  'people',
] as const;
export type BriefingField = (typeof BRIEFING_FIELDS)[number];

export interface CampaignBudget {
  amount: number;
  currency: string;
}

export interface CampaignMarket {
  country: string;
  languages: string[];
}

export interface AudienceSegment {
  ageFrom: number;
  ageTo: number;
  gender: AudienceGender;
}

/** One product the campaign advertises. The backend alone sets `versionId`. */
export interface BriefProduct {
  productId: string;
  isPrimary: boolean;
  destinationUrl: string;
  versionId: string;
}

export interface ReferenceMaterial {
  referenceId: string;
  objectName: string;
}

export interface CampaignBriefing {
  goal: CampaignGoal | null;
  startsOn: string;
  endsOn: string;
  budget: CampaignBudget | null;
  markets: CampaignMarket[];
  segments: AudienceSegment[];
  products: BriefProduct[];
  finalUrl: string;
  direction: string;
  angles: MessagingAngle[];
  angleOther: string;
  treatments: VisualTreatment[];
  references: ReferenceMaterial[];
  people: PeopleSetting | null;
  appliedSeasons: string[];
  /** The tags of the applied seasonal themes. The backend writes them. */
  seasonalTags: string[];
  extractedFields: BriefingField[];
}

export interface Campaign extends LiveRow {
  id: string;
  name: string;
  brandSlug: string;
  status: CampaignStatus;
  briefing: CampaignBriefing;
  /** Derived on every save from the fields that are filled. Never typed. */
  tags: string[];
  updatedAt: string;
  isActive: boolean;
}

export const EMPTY_BRIEFING: CampaignBriefing = {
  goal: null,
  startsOn: '',
  endsOn: '',
  budget: null,
  markets: [],
  segments: [],
  products: [],
  finalUrl: '',
  direction: '',
  angles: [],
  angleOther: '',
  treatments: [],
  references: [],
  people: null,
  appliedSeasons: [],
  seasonalTags: [],
  extractedFields: [],
};

function budget(value: unknown): CampaignBudget | null {
  const fields = readRecord(value);
  const amount = readNumber(fields.amount);
  const currency = readString(fields.currency);
  return currency ? { amount, currency } : null;
}

function markets(value: unknown): CampaignMarket[] {
  return readRecords(value)
    .filter((item) => readString(item.country))
    .map((item) => ({ country: readString(item.country), languages: readStringList(item.languages) }));
}

function segments(value: unknown): AudienceSegment[] {
  return readRecords(value).map((item) => ({
    ageFrom: readNumber(item.age_from),
    ageTo: readNumber(item.age_to),
    gender: readOneOf(AUDIENCE_GENDERS, item.gender, 'any'),
  }));
}

function products(value: unknown): BriefProduct[] {
  return readRecords(value)
    .filter((item) => readString(item.product_id))
    .map((item) => ({
      productId: readString(item.product_id),
      isPrimary: item.is_primary === true,
      destinationUrl: readString(item.destination_url),
      versionId: readString(item.version_id),
    }));
}

function references(value: unknown): ReferenceMaterial[] {
  return readRecords(value)
    .filter((item) => readString(item.reference_id) && readString(item.object_name))
    .map((item) => ({ referenceId: readString(item.reference_id), objectName: readString(item.object_name) }));
}

function enumList<T extends string>(values: readonly T[], value: unknown): T[] {
  return readStringList(value).filter((item): item is T => values.includes(item as T));
}

/** Returns the briefing of one campaign. A missing field falls back to empty. */
export function parseBriefing(raw: Record<string, unknown>): CampaignBriefing {
  return {
    goal: readNullableOneOf(CAMPAIGN_GOALS, raw.goal),
    startsOn: readString(raw.starts_on),
    endsOn: readString(raw.ends_on),
    budget: budget(raw.budget),
    markets: markets(raw.markets),
    segments: segments(raw.segments),
    products: products(raw.products),
    finalUrl: readString(raw.final_url),
    direction: readString(raw.direction),
    angles: enumList(MESSAGING_ANGLES, raw.angles),
    angleOther: readString(raw.angle_other),
    treatments: enumList(VISUAL_TREATMENTS, raw.treatments),
    references: references(raw.references),
    people: readNullableOneOf(PEOPLE_SETTINGS, raw.people),
    appliedSeasons: readStringList(raw.applied_seasons),
    seasonalTags: readStringList(raw.seasonal_tags),
    extractedFields: enumList(BRIEFING_FIELDS, raw.extracted_fields),
  };
}

/** Returns one campaign document, or null if the document has no name. */
export function parseCampaign(id: string, raw: Record<string, unknown>): Campaign | null {
  const name = readString(raw.name);
  if (!name) return null;
  const status = readOneOf(CAMPAIGN_STATUSES, raw.status, 'draft');
  return {
    id,
    name,
    brandSlug: readString(raw.brand_slug),
    status,
    briefing: parseBriefing(readRecord(raw.briefing)),
    tags: readStringList(raw.tags),
    updatedAt: readString(readRecord(raw.metadata).updated_at),
    isActive: status !== 'deactivated',
  };
}

/** What a campaign write returns. The Firestore stream confirms the write a moment later. */
export interface CampaignWrite {
  slug: string;
  name: string;
  status: CampaignStatus;
  missing: string[];
  isComplete: boolean;
  briefing: CampaignBriefing | null;
}

/** Returns one campaign write response, or null when it has no slug. */
export function parseCampaignWrite(response: unknown): CampaignWrite | null {
  const raw = readRecord(response);
  const slug = readString(raw.slug);
  if (!slug) return null;
  return {
    slug,
    name: readString(raw.name),
    status: readOneOf(CAMPAIGN_STATUSES, raw.status, 'draft'),
    missing: readStringList(raw.missing),
    isComplete: raw.is_complete === true,
    briefing: raw.briefing === undefined ? null : parseBriefing(readRecord(raw.briefing)),
  };
}
